import { useCallback, useMemo } from "react";
import { animateScrollTop } from "./scroll-controller-animation";
import { SCROLL_TARGET_KIND } from "./scroll-controller-constants";
import { resolveLatestMessageAnchorTop } from "./scroll-controller-latest-anchor";
import {
  createPendingBottomScrollRequest,
  createPendingTargetScrollRequest,
} from "./scroll-controller-requests";
import { scrollToRegisteredTarget } from "./scroll-controller-target";
import type {
  PendingScrollRequest,
  QueuePendingTargetScrollInput,
  ScrollBottomOptions,
  ScrollTargetOptions,
  ScrollToRegisteredElementInput,
} from "./scroll-controller-types";
import type { ScrollControllerRefs } from "./use-scroll-controller-refs";

export interface ScrollActions {
  cancelAutoScroll: () => void;
  scrollToBottom: (opts?: ScrollBottomOptions) => void;
  scrollToMessageId: (messageId: string, opts?: ScrollTargetOptions) => void;
  scrollToVersionId: (
    versionShortId: string,
    opts?: ScrollTargetOptions,
  ) => void;
}

interface UseScrollActionsInput {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  smoothScroll: boolean;
  refs: ScrollControllerRefs;
  setShowJumpToLatest: (show: boolean) => void;
}

export function useScrollActions({
  scrollRef,
  smoothScroll,
  refs,
  setShowJumpToLatest,
}: UseScrollActionsInput): ScrollActions {
  const {
    isAutoScrollingRef,
    messageElementsRef,
    pendingScrollRef,
    runIdRef,
    versionElementsRef,
  } = refs;
  const cancelAutoScroll = useCancelAutoScroll({
    isAutoScrollingRef,
    pendingScrollRef,
    runIdRef,
  });
  const scrollToTopValue = useScrollToTopValue({
    scrollRef,
    isAutoScrollingRef,
    runIdRef,
    smoothScroll,
  });
  const queuePendingTargetScroll = useQueuePendingTargetScroll({
    pendingScrollRef,
  });
  const scrollToRegisteredElement = useScrollToRegisteredElement({
    scrollRef,
    queuePendingTargetScroll,
    scrollToTopValue,
  });
  const scrollToBottom = useScrollToBottom({
    scrollRef,
    messageElementsRef,
    pendingScrollRef,
    scrollToTopValue,
    setShowJumpToLatest,
  });
  const scrollToMessageId = useScrollToMessageId({
    messageElementsRef,
    scrollToRegisteredElement,
  });
  const scrollToVersionId = useScrollToVersionId({
    versionElementsRef,
    scrollToRegisteredElement,
  });

  return useMemo(
    () => ({
      cancelAutoScroll,
      scrollToBottom,
      scrollToMessageId,
      scrollToVersionId,
    }),
    [cancelAutoScroll, scrollToBottom, scrollToMessageId, scrollToVersionId],
  );
}

function useCancelAutoScroll({
  isAutoScrollingRef,
  pendingScrollRef,
  runIdRef,
}: Pick<
  ScrollControllerRefs,
  "isAutoScrollingRef" | "pendingScrollRef" | "runIdRef"
>): () => void {
  return useCallback(() => {
    runIdRef.current += 1;
    isAutoScrollingRef.current = false;
    pendingScrollRef.current = null;
  }, [isAutoScrollingRef, pendingScrollRef, runIdRef]);
}

function useScrollToTopValue({
  scrollRef,
  isAutoScrollingRef,
  runIdRef,
  smoothScroll,
}: Pick<UseScrollActionsInput, "scrollRef" | "smoothScroll"> &
  Pick<ScrollControllerRefs, "isAutoScrollingRef" | "runIdRef">): (
  targetTop: number,
  instant: boolean,
) => void {
  return useCallback(
    (targetTop: number, instant: boolean) => {
      animateScrollTop({
        scrollRef,
        runIdRef,
        isAutoScrollingRef,
        smoothScroll,
        targetTop,
        instant,
      });
    },
    [scrollRef, runIdRef, isAutoScrollingRef, smoothScroll],
  );
}

function useQueuePendingTargetScroll({
  pendingScrollRef,
}: {
  pendingScrollRef: React.RefObject<PendingScrollRequest | null>;
}): (input: QueuePendingTargetScrollInput) => void {
  return useCallback(
    ({ targetId, targetKind, opts }: QueuePendingTargetScrollInput): void => {
      pendingScrollRef.current = createPendingTargetScrollRequest({
        targetId,
        targetKind,
        opts,
      });
    },
    [pendingScrollRef],
  );
}

function useScrollToRegisteredElement({
  scrollRef,
  queuePendingTargetScroll,
  scrollToTopValue,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  queuePendingTargetScroll: (input: QueuePendingTargetScrollInput) => void;
  scrollToTopValue: (targetTop: number, instant: boolean) => void;
}): (input: ScrollToRegisteredElementInput) => void {
  return useCallback(
    (input: ScrollToRegisteredElementInput): void => {
      scrollToRegisteredTarget({
        ...input,
        scrollRef,
        queuePendingTargetScroll,
        scrollToTopValue,
      });
    },
    [queuePendingTargetScroll, scrollRef, scrollToTopValue],
  );
}

function useScrollToBottom({
  scrollRef,
  messageElementsRef,
  pendingScrollRef,
  scrollToTopValue,
  setShowJumpToLatest,
}: Pick<UseScrollActionsInput, "scrollRef" | "setShowJumpToLatest"> & {
  messageElementsRef: React.RefObject<Map<string, HTMLElement>>;
  pendingScrollRef: React.RefObject<PendingScrollRequest | null>;
  scrollToTopValue: (targetTop: number, instant: boolean) => void;
}): (opts?: ScrollBottomOptions) => void {
  return useCallback(
    (opts?: ScrollBottomOptions) => {
      const el = scrollRef.current;
      if (!el) {
        pendingScrollRef.current = createPendingBottomScrollRequest(opts);
        return;
      }

      scrollToTopValue(
        resolveLatestMessageAnchorTop({ el, messageElementsRef }),
        Boolean(opts?.instant),
      );
      setShowJumpToLatest(false);
    },
    [
      scrollRef,
      messageElementsRef,
      pendingScrollRef,
      scrollToTopValue,
      setShowJumpToLatest,
    ],
  );
}

function useScrollToMessageId({
  messageElementsRef,
  scrollToRegisteredElement,
}: {
  messageElementsRef: React.RefObject<Map<string, HTMLElement>>;
  scrollToRegisteredElement: (input: ScrollToRegisteredElementInput) => void;
}): (messageId: string, opts?: ScrollTargetOptions) => void {
  return useCallback(
    (messageId: string, opts?: ScrollTargetOptions) => {
      scrollToRegisteredElement({
        targetId: messageId,
        targetKind: SCROLL_TARGET_KIND.Message,
        targetElements: messageElementsRef,
        opts,
      });
    },
    [messageElementsRef, scrollToRegisteredElement],
  );
}

function useScrollToVersionId({
  versionElementsRef,
  scrollToRegisteredElement,
}: {
  versionElementsRef: React.RefObject<Map<string, HTMLElement>>;
  scrollToRegisteredElement: (input: ScrollToRegisteredElementInput) => void;
}): (versionShortId: string, opts?: ScrollTargetOptions) => void {
  return useCallback(
    (versionShortId: string, opts?: ScrollTargetOptions) => {
      scrollToRegisteredElement({
        targetId: versionShortId,
        targetKind: SCROLL_TARGET_KIND.Version,
        targetElements: versionElementsRef,
        opts,
      });
    },
    [versionElementsRef, scrollToRegisteredElement],
  );
}
